/**
 * Custom hook for handling user logout with complete state cleanup.
 *
 * Clears:
 * - React Query cache (all queries)
 * - Zustand stores (UI, Loading, PRD, SSE)
 * - localStorage (app-specific data)
 * - Firebase authentication
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useLoadingStore } from '../stores/loadingStore';
import { usePRDStore } from '../stores/prdStore';
import { useSSEStore } from '../stores/sseStore';
import { useUIStore } from '../stores/uiStore';
import { clearActiveOrgId } from '../utils/activeOrg';
import { logger } from '../utils/logger';
import { clearAll } from '../utils/storage';

import { useAuth } from './useAuth';

/**
 * Custom hook that provides a logout function with complete state cleanup.
 *
 * @returns Logout function that clears all user-specific state and signs out
 *
 * @example
 * ```tsx
 * const { handleLogout } = useLogout();
 * <button onClick={handleLogout}>Logout</button>
 * ```
 */
export function useLogout(): {
  handleLogout: () => Promise<void>;
} {
  const { signOut } = useAuth();
  const queryClient = useQueryClient();

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      logger.info({ component: 'useLogout' }, 'Starting logout process');

      // 1. Sign out from Firebase FIRST (this will trigger auth state change)
      logger.debug({ component: 'useLogout' }, 'Signing out from Firebase');
      await signOut();

      // 2. Clear localStorage (app-specific data)
      logger.debug({ component: 'useLogout' }, 'Clearing localStorage');
      const clearedCount = clearAll();
      // Also clear activeOrgId (not prefixed)
      clearActiveOrgId();
      logger.debug({ component: 'useLogout' }, 'Cleared localStorage items', {
        count: clearedCount,
      });

      // 3. Clear React Query cache (after navigation, to avoid refetches)
      // Note: This is done AFTER signOut to prevent API calls during logout
      logger.debug({ component: 'useLogout' }, 'Clearing React Query cache');
      queryClient.clear();

      // 4. Reset UI store to initial state
      logger.debug({ component: 'useLogout' }, 'Resetting UI store');
      const { setEditingProject, setEditingTask, setSelectedProject, setSelectedTask } =
        useUIStore.getState();
      setSelectedProject(null);
      setSelectedTask(null);
      setEditingProject(null);
      setEditingTask(null);

      // 5. Clear loading store optimistic updates
      logger.debug({ component: 'useLogout' }, 'Clearing loading store');
      useLoadingStore.getState().clearAllOptimisticUpdates();

      // 6. Clear PRD store sessions
      logger.debug({ component: 'useLogout' }, 'Clearing PRD store sessions');
      usePRDStore.getState().clearSession();

      // 7. Clear SSE store events and jobs, reset connection status
      logger.debug({ component: 'useLogout' }, 'Clearing SSE store');
      const sseStore = useSSEStore.getState();
      sseStore.clearAllEvents();
      sseStore.setConnectionStatus('disconnected');

      logger.info({ component: 'useLogout' }, 'Logout completed successfully');
    } catch (error) {
      logger.error(
        { component: 'useLogout' },
        'Error during logout',
        error instanceof Error ? error : new Error(String(error)),
      );
      // Re-throw to allow caller to handle
      throw error;
    }
  }, [signOut, queryClient]);

  return { handleLogout };
}
